import { useQueries } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import useUploadFileMutation from 'api/hooks/useUploadFileMutation';
import { useMutationHTTPRequest } from 'shared_DEPRECATED/hooks';
import { Attachment } from 'shared_DEPRECATED/types/Attachment';

import { SessionRecapAttachmentType } from '../config/types';

export const useAttachmentsUpload = ({
  attachments,
  onSuccess,
  onError,
}: {
  attachments: SessionRecapAttachmentType[];
  onSuccess?: (attachment: SessionRecapAttachmentType[]) => void;
  onError?: () => void;
}) => {
  const [successfulAttachments, setSuccessfulAttachments] = useState(
    [] as SessionRecapAttachmentType[]
  );
  const [successfulMutations, setSuccessfulMutations] = useState(
    [] as Attachment[]
  );
  const [attachmentsQueue, setAttachmentsQueue] = useState(
    [] as SessionRecapAttachmentType[]
  );

  const { request } = useMutationHTTPRequest();

  const mutation = useUploadFileMutation({
    onSuccess: () => {
      const attachment = attachmentsQueue[0];

      setSuccessfulMutations((oldMutations) => [
        ...oldMutations,
        {
          url: attachment.url!,
          name: attachment.name,
          contentType: attachment.contentType,
          contentLength: attachment.contentLength,
          key: attachment.attachmentId,
        },
      ]);
      setAttachmentsQueue((oldQueue) => oldQueue.slice(1));
    },
  });

  const attachmentsQueries = useQueries({
    queries: attachments.map((attachment) => {
      return {
        queryKey: ['attachment', attachment.name],
        useErrorBoundary: false,
        queryFn: async () => {
          const response = await request({
            url: '/api/attachments/upload',
            headers: {
              'object-content-type': attachment.contentType,
            },
          });

          setSuccessfulAttachments((prev) => [
            ...prev,
            {
              ...attachment,
              url: response.presignedUrl,
              attachmentId: response.attachmentId,
            },
          ]);
          return response;
        },
        onSuccess: (data: { key: string; presignedUrl: string }) => {
          const newAttachment = {
            ...attachment,
            url: data.presignedUrl,
            key: data.key,
          };
          setAttachmentsQueue((oldQueue) => [...oldQueue, newAttachment]);
        },
        onError: () => {
          if (onError) {
            onError();
          }
        },
      };
    }),
  });

  useEffect(() => {
    if (attachmentsQueue.length > 0 && !mutation.isLoading) {
      mutation.mutate({
        file: attachmentsQueue[0].file!,
        key: attachmentsQueue[0].attachmentId as string,
        presignedUrl: attachmentsQueue[0].url!,
      });
    }
  }, [attachmentsQueue, mutation]);

  useEffect(() => {
    if (
      successfulAttachments.length &&
      successfulMutations.length &&
      successfulAttachments.length === successfulMutations.length
    ) {
      const uploadedAtt = attachments.map((attachment) => {
        const successAttachment = successfulAttachments.find(
          (successAtt) => successAtt.id === attachment.id
        );
        return {
          name: attachment.name,
          contentType: attachment.contentType,
          contentLength: attachment.contentLength,
          duration: attachment.duration,
          url: successAttachment?.url,
          attachmentId: successAttachment?.attachmentId,
          id: attachment.id,
          // TODO: remove key after BE removes in on their side
          key: successAttachment?.attachmentId,
        };
      });

      setSuccessfulAttachments([]);
      setSuccessfulMutations([]);

      if (onSuccess) {
        onSuccess(uploadedAtt);
      }
    }
  }, [successfulMutations, successfulAttachments, attachments, onSuccess]);

  return attachmentsQueries;
};
